import AppDialog from '../../../../../components/common/AppDialog';
import AppButton from '../../../../../components/common/AppButton';
import { SVG } from '../../../../../constants/assetLists';
import { z } from 'zod';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiResponse } from '../../../../../services/axiosInstance';
import QUERY_KEYS from '../../../../../constants/queryKeys';
import {
  SubBranchData,
  useClientsContext,
} from '../../../../../context/clients/ClientsContextProvider';
import { useDebounce } from '../../../../../hooks/useDebunce';
import { handleGetAllUsersService } from '../../../../../services/users';
import { UsersListResponse } from '../../../../users/components/UserScreens/users';
import UsersList from '../../../../users/components/GroupScreens/components/UsersList';
import { useTranslation } from 'react-i18next';
import BranchTreeAutocomplete from '../../../../../components/common/BranchTreeAutocomplete';
import AppMUIAutocomplete from '../../../../../components/common/AppMUIAutoComplete';
import { useFormsMutations } from '../../../../../hooks/useFormsHook';
import { Checkbox, Radio } from '@mui/material';

const zodSchema = z
  .object({
    respondent: z.string().min(1, 'סוג המשיב הוא שדה חובה'),
    branchIds: z.array(z.string()),
    subBranchIds: z.array(z.string()),
    userIds: z.array(z.string()),
    sendLink: z.boolean(),
    shareLinkVia: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.respondent === 'restricted' && data.userIds.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'יש לבחור לפחות משתמש אחד עבור גישה מוגבלת',
        path: ['userIds'],
      });
    }
  });

export type UsersGroupFormValues = z.infer<typeof zodSchema>;

type Props = {
  open: boolean;
  onClose: () => void;
  formData: any;
};

function FormShareDialog({ open, onClose, formData }: Props) {
  const { t } = useTranslation();

  const RESPONDENT_OPTIONS = [
    { label: t('forms.dialog.share.anyoneWithLink'), value: 'anyone' },
    { label: t('forms.dialog.share.restricted'), value: 'restricted' },
  ];

  const { allClients } = useClientsContext();
  const { shareFormMutation } = useFormsMutations();

  const [searchText, setSearchText] = useState('');

  const debouncedSearch = useDebounce(searchText, 500);

  const method = useForm<UsersGroupFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      respondent: '',
      branchIds: [],
      subBranchIds: [],
      userIds: [],
      sendLink: false,
      shareLinkVia: '',
    },
    resolver: zodResolver(zodSchema),
  });

  const {
    formState: { errors, isSubmitting },
    reset,
    control,
    setValue,
    handleSubmit,
    watch,
    getValues,
  } = method;

  const clientIds = formData?.clients?.map((c: any) => c?._id) || [];

  const users = watch('userIds');
  const branchIds = watch('branchIds');
  const subBranchIds = watch('subBranchIds');

  const { data: allUsers } = useQuery<
    ApiResponse<UsersListResponse>,
    Error,
    UsersListResponse
  >({
    queryKey: [
      QUERY_KEYS.GET_ALL_USERS,
      clientIds,
      branchIds,
      subBranchIds,
      debouncedSearch,
    ],
    queryFn: () =>
      handleGetAllUsersService({
        search: debouncedSearch,
        status: 'active',
        clientId: clientIds,
        ...(branchIds?.length > 0 && { branchId: branchIds }),
        ...(subBranchIds?.length > 0 && { subBranchId: subBranchIds }),
      }),
    enabled: !!open && !!clientIds.length,
  });

  const clientsArr = (allClients ?? []).filter((client) =>
    clientIds.includes(client.id),
  );

  useEffect(() => {
    if (open && formData) {
      setValue('respondent', formData?.isAnonymous ? 'anyone' : 'restricted');
      setValue('branchIds', formData?.branches?.map((b: any) => b?._id) || []);
      setValue(
        'subBranchIds',
        formData?.subBranches?.map((b: any) => b?._id) || [],
      );
      setValue(
        'userIds',
        formData?.recipients?.users?.map((user: any) => user?._id) || [],
      );
    }
  }, [open, formData]);

  const handleModalClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: UsersGroupFormValues) => {
    try {
      await shareFormMutation.mutateAsync({
        formId: formData?._id,
        payload: {
          isAnonymous: data.respondent === 'anyone',
          clientIds,
          userIds: data.userIds,
          branchIds: data.branchIds,
          subBranchIds: data.subBranchIds,
          url: `${window.location.origin}/form/${formData?._id}`,
          shareLinkVia: data.sendLink ? data.shareLinkVia : undefined,
        },
      });
      handleModalClose();
    } catch {
      // Error is handled by the global error handler in QueryProvider
    }
  };

  return (
    <AppDialog
      showModel={open}
      title={`${t('forms.dialog.responses.share')} ${formData?.title || ''}`}
      titleIcon={
        <img
          src={SVG.darkStarIcon}
          alt="icon"
          className="w-[20px] h-[20px] object-contain"
        />
      }
      onClose={handleModalClose}
      showHeaderLine={false}
      width={717}
    >
      <div className="flex flex-col gap-4 pt-4">
        <div className="flex-1 min-h-0 flex flex-col gap-4 px-[40px] overflow-y-auto">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2">
              <p className="m-0 text-xl text-black">
                {t('forms.dialog.share.formCreation')}
              </p>

              <div>
                <p className="m-0 text-black">
                  {`${formData?.createdBy?.firstName ?? ''} ${formData?.createdBy?.lastName ?? ''}`.trim()}
                </p>
                <p className="m-0 text-black">
                  {formData?.createdBy?.email ?? ''}
                </p>
              </div>
            </div>

            <Controller
              name="respondent"
              control={control}
              render={({ field: { value } }) => (
                <div className="flex flex-col gap-1">
                  <p className="m-0 text-xl text-black">
                    {t('forms.dialog.share.respondents')}
                  </p>

                  <AppMUIAutocomplete
                    label={t('forms.dialog.share.respondentsDisplay')}
                    options={RESPONDENT_OPTIONS}
                    getOptionLabel={(option) => option.label}
                    size="small"
                    fullWidth
                    value={
                      RESPONDENT_OPTIONS.find(
                        (option) => option.value === value,
                      ) || null
                    }
                    onChange={(__, val) =>
                      setValue('respondent', val ? val.value : '', {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                    error={!!errors.respondent}
                    helperText={errors.respondent?.message}
                  />
                </div>
              )}
            />

            {clientsArr?.some(
              (c) =>
                c.branches?.length > 0 ||
                c.branches?.some((b: any) => (b.subBranches?.length ?? 0) > 0),
            ) && (
              <Controller
                name="branchIds"
                control={control}
                render={({ field: { value: branchIdsValue } }) => {
                  return (
                    <div className="flex flex-col gap-1">
                      <p className="m-0 text-xl text-black">
                        {t('forms.dialog.share.sendFormTo')}
                      </p>

                      <BranchTreeAutocomplete
                        label={t('forms.dialog.share.associateToOrg')}
                        allClients={clientsArr}
                        showClientChip
                        value={[
                          ...(branchIdsValue || []),
                          ...(subBranchIds || []),
                        ]}
                        onChange={(val) => {
                          const prevBranchIds = getValues('branchIds') || [];

                          const newBranchIds =
                            val
                              ?.filter((item) => item?.isBranch)
                              ?.map((item) => item?.id) || [];
                          const newSubBranches = (val?.filter(
                            (item) => item?.isSubBranch,
                          ) || []) as SubBranchData[];

                          const removedBranchIds = prevBranchIds.filter(
                            (id) => !newBranchIds.includes(id),
                          );

                          const filteredSubBranches = newSubBranches.filter(
                            (sub) => !removedBranchIds.includes(sub.branchId),
                          );

                          const autoAddBranchIds = filteredSubBranches
                            .filter(
                              (sub) => !newBranchIds.includes(sub.branchId),
                            )
                            .map((sub) => sub.branchId);

                          const finalBranchIds = Array.from(
                            new Set([...newBranchIds, ...autoAddBranchIds]),
                          );
                          const finalSubBranchIds = filteredSubBranches.map(
                            (sub) => sub.id,
                          );

                          setValue('branchIds', finalBranchIds, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          setValue('subBranchIds', finalSubBranchIds, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          setValue('userIds', []);
                        }}
                      />
                    </div>
                  );
                }}
              />
            )}
          </div>

          <FormProvider {...method}>
            <UsersList
              allUsers={allUsers}
              searchText={searchText}
              setSearchText={setSearchText}
              debouncedSearch={debouncedSearch}
              users={users}
              marginStart={false}
              onUserSelection={(ids) => {
                setValue('userIds', ids?.map(String) || [], {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
              error={errors.userIds?.message}
              maxHeight="260px"
            />
          </FormProvider>

          <div className="flex items-start gap-3">
            <Controller
              name="sendLink"
              control={control}
              render={({ field }) => {
                return (
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        color="secondary"
                        size="small"
                        sx={{ p: 1 }}
                        checked={field.value}
                        value={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          setValue('shareLinkVia', '', {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }}
                      />
                      <p className="m-0">שתף אל</p>
                    </div>

                    {errors?.shareLinkVia?.message && (
                      <span className="text-red-400 text-[12px]">
                        {errors?.shareLinkVia?.message}
                      </span>
                    )}
                  </div>
                );
              }}
            />

            <Controller
              name="shareLinkVia"
              control={control}
              render={({ field }) => {
                const isDisabled = !watch('sendLink');
                return (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Radio
                        color="secondary"
                        size="small"
                        sx={{ p: 1 }}
                        disabled={isDisabled}
                        value={'email'}
                        checked={field.value == 'email'}
                        onChange={(e) => field.onChange(e.target.value)}
                      />

                      <div className="flex items-center gap-2">
                        <img
                          src={SVG.emailIcon}
                          alt="email icon"
                          className="w-[20px] h-[20px] object-contain"
                        />
                        <p className="m-0">לאימייל</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Radio
                        color="secondary"
                        size="small"
                        sx={{ p: 1 }}
                        disabled={isDisabled}
                        value={'sms'}
                        checked={field.value == 'sms'}
                        onChange={(e) => field.onChange(e.target.value)}
                      />

                      <div className="flex items-center gap-2">
                        <img
                          src={SVG.whatsAppIcon}
                          alt="whatsapp icon"
                          className="w-[20px] h-[20px] object-contain"
                        />
                        <p className="m-0">לטלפון</p>
                      </div>
                    </div>
                  </div>
                );
              }}
            />
          </div>
        </div>

        <div className="flex justify-end px-[48px]">
          <div className="flex items-center gap-2">
            <AppButton onClick={handleModalClose}>
              {t('forms.dialog.share.cancel')}
            </AppButton>

            <AppButton
              variant="primary"
              isLoading={isSubmitting}
              onClick={handleSubmit(onSubmit)}
            >
              {t('forms.dialog.share.save')}
            </AppButton>
          </div>
        </div>
      </div>
    </AppDialog>
  );
}

export default FormShareDialog;
